<template>
  <div id="app" :class="{ site: !isAdminRoute }">
    <NavigationBar v-if="!isAdminRoute" />
    <transition :css="false" mode="out-in" @leave="onLeave" @enter="onEnter">
      <router-view :key="$route.path" />
    </transition>
    <div ref="curtain" class="curtain" aria-hidden="true">
      <img class="curtain__logo" src="./assets/logotipo-short.webp" alt="">
    </div>
    <CustomCursor v-if="!isAdminRoute" />
  </div>
</template>

<script>
import NavigationBar from '@/components/NavigationBar'
import CustomCursor from '@/components/ui/CustomCursor.vue'
import { gsap, ScrollTrigger, prefersReducedMotion } from '@/animations/gsap'
import {
  initSmoothScroll,
  destroySmoothScroll,
  lockScroll,
  unlockScroll,
  scrollToTop
} from '@/animations/smoothScroll'
import { setCursorLabel } from '@/animations/cursor'

export default {
  name: 'App',
  components: {
    NavigationBar,
    CustomCursor
  },
  data() {
    return { firstEnter: true }
  },
  computed: {
    isAdminRoute() {
      return this.$route.path === '/admin'
    }
  },
  watch: {
    // El admin usa diálogos de Element con scroll propio: sin Lenis ahí.
    isAdminRoute: {
      immediate: true,
      handler(admin) {
        if (admin) destroySmoothScroll()
        else initSmoothScroll()
      }
    }
  },
  beforeDestroy() {
    destroySmoothScroll()
  },
  methods: {
    // Cortina roja entre páginas (la primera carga la maneja la intro del hero).
    onLeave(el, done) {
      setCursorLabel('')
      if (prefersReducedMotion()) {
        done()
        return
      }
      lockScroll()
      this.leaving = true
      const curtain = this.$refs.curtain
      gsap.timeline({ onComplete: done })
        .set(curtain, { visibility: 'visible' })
        .to(el, { y: -60, opacity: 0, duration: 0.6, ease: 'power2.in' }, 0)
        .fromTo(curtain, { scaleY: 0, transformOrigin: 'bottom' }, { scaleY: 1, duration: 0.7, ease: 'expo.inOut' }, 0.1)
        .fromTo(curtain.firstChild, { yPercent: 60, opacity: 0 }, { yPercent: 0, opacity: 1, duration: 0.6, ease: 'expo.out' }, 0.45)
    },
    onEnter(el, done) {
      if (this.firstEnter || !this.leaving) {
        this.firstEnter = false
        done()
        return
      }
      this.leaving = false
      const curtain = this.$refs.curtain
      const finish = () => {
        gsap.set(curtain, { visibility: 'hidden', clearProps: 'transform' })
        gsap.set(el, { clearProps: 'transform,opacity' })
        unlockScroll()
        done()
      }
      scrollToTop()
      requestAnimationFrame(() => {
        ScrollTrigger.refresh()
        gsap.timeline({ onComplete: finish })
          .to(curtain.firstChild, { yPercent: -60, opacity: 0, duration: 0.4, ease: 'power2.in' }, 0)
          .to(curtain, { scaleY: 0, transformOrigin: 'top', duration: 0.8, ease: 'expo.inOut' }, 0.15)
          .from(el, { y: 60, opacity: 0, duration: 0.9, ease: 'expo.out' }, 0.45)
      })
    }
  }
}
</script>

<style src="./styles/base.css"></style>

<style scoped>
.curtain {
  position: fixed;
  inset: 0;
  z-index: var(--z-transition);
  display: grid;
  place-items: center;
  background: var(--c-accent);
  visibility: hidden;
  pointer-events: none;
}

.curtain__logo {
  width: clamp(10rem, 6rem + 12vw, 22rem);
  height: auto;
}
</style>
